import { DefaultOptionType } from 'antd/lib/select';
import { AlfredTableColumn } from 'components/organisms/AlfredTable';
import TagsListManager from 'components/organisms/TagsListManager';
import MenuAtom from 'components/atoms/MenuAtom';
import SwitchAtom from 'components/atoms/SwitchAtom';
import ContextualMenu from 'components/atoms/ContextualMenu';
import SpaceAtom from 'components/atoms/SpaceAtom';
import { Icon } from 'components/atoms/Icons';
import { CustomSensorModel } from 'components/pages/AssetPage/Tabs/TabConsumption/OptionList/resources/models';
import './styles.scss';

interface SensorColumnsProps {
  sensorColumnsLiterals: Record<string, string>;
  temporalTagList: string[];
  onOpenTagEditor: (id: string) => void;
  onOpenModal: (sensor: CustomSensorModel) => void;
  onSaveTagList: (sensor: CustomSensorModel, newTags: string[]) => void;
  onEditSensor: (id: string, sensor: CustomSensorModel) => void;
  onChangeTagList: (values: string[] | null) => void;
  onCloseTagEditor: () => void;
  onFilterTagList: (input: string, option?: DefaultOptionType) => boolean;
  isSavingTagList: boolean;
  isUserSuperAdmin: boolean;
}

export const SensorColumns = ({
  temporalTagList,
  sensorColumnsLiterals: {
    actionsText,
    editNameText,
    editTagButtonText,
    editTagPlaceholder,
    nameText,
    roomText,
    saveTagButtonText,
    cancelTagButtonText,
    showInStatisticsText,
    statisticsText,
    tagText,
    emptyTagListText,
    typeText,
    usageText,
  },
  onOpenTagEditor,
  onSaveTagList,
  onCloseTagEditor,
  onEditSensor,
  onOpenModal,
  onChangeTagList,
  onFilterTagList,
  isSavingTagList,
  isUserSuperAdmin,
}: SensorColumnsProps): AlfredTableColumn<CustomSensorModel>[] => {
  return [
    {
      title: nameText,
      width: '20%',
      key: 'name',
      render: ({ id, name }) => (
        <>
          <div>{name}</div>
          <div className={'SensorColumns__id'}>{id}</div>
        </>
      ),
    },
    {
      title: typeText,
      width: '10%',
      key: 'sensorType',
      render: ({ sensorType }) => sensorType,
    },
    {
      title: tagText,
      width: 450,
      className: 'SensorColumns__tag-column',
      key: 'tag',
      render: (sensor: CustomSensorModel) =>
        <TagsListManager
          tagList={sensor.tags}
          temporalTagList={temporalTagList}
          emptyTagListText={emptyTagListText}
          sensor={sensor}
          placeholder={editTagPlaceholder}
          availableTags={sensor.parsedAvailableTags}
          saveButtonText={saveTagButtonText}
          cancelButtonText={cancelTagButtonText}
          onSave={onSaveTagList}
          onCloseTagEditor={onCloseTagEditor}
          onChangeTagList={onChangeTagList}
          onFilterTagList={onFilterTagList}
          isSavingTagsList={isSavingTagList}
          allowClear={true}
          selectMode={'multiple'}
        />
    },
    {
      title: roomText,
      width: '10%',
      key: 'room',
      render: ({ room }) => room,
    },
    {
      title: usageText,
      width: '10%',
      key: 'usage',
      render: ({ usage }) => usage,
    },
    {
      title: statisticsText,
      width: '5%',
      key: 'statistics',
      render: ({ canDataBeFetched }) =>
        canDataBeFetched
          ? <Icon.Show className='SensorColumns__icon' />
          : <Icon.Hide className='SensorColumns__icon' />
    },
    {
      title: actionsText,
      key: 'menu',
      width: '5%',
      render: (sensor: CustomSensorModel) => <ContextualMenu menu={(
        <MenuAtom
          disabled={sensor.disabledRowSelection}
          items={[
            {
              key: 'edit_name',
              label: editNameText,
              onClick: () => onOpenModal(sensor),
            },
            {
              key: 'is_visible',
              onClick: () => onEditSensor(sensor.id, { ...sensor, canDataBeFetched: !sensor.canDataBeFetched }),
              label: (
                <SpaceAtom>
                  <SwitchAtom size={'small'} checked={sensor.canDataBeFetched} />
                  {showInStatisticsText}
                </SpaceAtom>
              )
            },
            isUserSuperAdmin ? {
              key: 'edit_tag',
              onClick: () => onOpenTagEditor(sensor.id),
              disabled: sensor.isEditingTagList,
              label: editTagButtonText,
            } : null,
          ]}
        />
      )} />,
    },
  ];
};
